import { apiClient } from '../../../lib/api/client';
import { resolveProblemDetailsMessage } from '../../../lib/api/problemMessage';
import type { ProblemDetails } from '../../../lib/api/types';

/** Matches backend Domain.Users.AccountStatus */
export const AccountStatus = {
  Pending: 0,
  Active: 1,
} as const;

export type AccountStatusValue = (typeof AccountStatus)[keyof typeof AccountStatus];

export interface CurrentUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  twoFactorEnabled: boolean;
  accountStatus?: AccountStatusValue;
  customerSegmentIds?: string[];
  tenantId?: string;
  isHost?: boolean;
  licenseWarning?: boolean;
  isImpersonating?: boolean;
  impersonatorUserId?: string;
  viewingCompanyName?: string;
}

export type GetCurrentUserResult =
  | { success: true; user: CurrentUser }
  | { success: false; error: string };

export async function getCurrentUser(): Promise<GetCurrentUserResult> {
  try {
    const response = await apiClient.get<CurrentUser>('/users/me');
    const user = response.data;
    if (!user || typeof user !== 'object' || !user.id || !user.email) {
      return { success: false, error: 'Invalid response from server' };
    }
    return {
      success: true,
      user: {
        id: user.id,
        email: user.email,
        firstName: user.firstName ?? '',
        lastName: user.lastName ?? '',
        twoFactorEnabled: Boolean((user as { twoFactorEnabled?: boolean }).twoFactorEnabled),
        accountStatus: (user as { accountStatus?: AccountStatusValue }).accountStatus,
        customerSegmentIds: (user as { customerSegmentIds?: string[] }).customerSegmentIds ?? [],
        tenantId: (user as { tenantId?: string }).tenantId,
        isHost: Boolean((user as { isHost?: boolean }).isHost),
        licenseWarning: Boolean((user as { licenseWarning?: boolean }).licenseWarning),
        isImpersonating: Boolean((user as { isImpersonating?: boolean }).isImpersonating),
        impersonatorUserId: (user as { impersonatorUserId?: string }).impersonatorUserId,
        viewingCompanyName: (user as { viewingCompanyName?: string }).viewingCompanyName,
      },
    };
  } catch (err: unknown) {
    if (err && typeof err === 'object' && 'response' in err) {
      const axiosError = err as { response?: { data?: ProblemDetails; status?: number } };
      const status = axiosError.response?.status;
      if (status === 401 || status === 403) {
        return { success: false, error: 'Unauthorized' };
      }
      if (status === 404) {
        return { success: false, error: 'User not found' };
      }
      const data = axiosError.response?.data;
      if (data && typeof data === 'object' && ('detail' in data || 'title' in data)) {
        return { success: false, error: resolveProblemDetailsMessage(data, 'Failed to load user') };
      }
    }
    return { success: false, error: 'Network error. Please try again.' };
  }
}

export interface AccessTokenResponse {
  accessToken: string;
  expiresInSeconds: number;
  csrfToken: string;
}

export interface LoginCredentials {
  email: string;
  password: string;
}

export type LoginResult =
  | { success: true; accessToken: string; csrfToken: string; expiresInSeconds: number }
  | { success: true; otpRequired: true; otpSessionId: string }
  | { success: false; error: string };

export async function forgotPasswordApi(
  email: string
): Promise<{ success: true; message: string } | { success: false; error: string }> {
  try {
    const response = await apiClient.post<{ message: string }>('/users/forgot-password', { email });
    return {
      success: true,
      message: response.data.message ?? 'If an account exists for that email, you will receive instructions shortly.',
    };
  } catch (err: unknown) {
    return { success: false, error: extractErrorMessage(err, 'Request failed') };
  }
}

export async function setPasswordApi(request: {
  token: string;
  newPassword: string;
  confirmPassword: string;
}): Promise<{ success: true } | { success: false; error: string }> {
  try {
    await apiClient.post('/users/set-password', request);
    return { success: true };
  } catch (err: unknown) {
    return { success: false, error: extractErrorMessage(err, 'Failed to set password') };
  }
}

export async function changePasswordApi(request: {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}): Promise<
  | { success: true; accessToken: string; csrfToken: string }
  | { success: false; error: string }
> {
  try {
    const response = await apiClient.post<AccessTokenResponse>('/users/me/change-password', request);
    const data = response.data;
    if (!data?.accessToken || !data?.csrfToken) {
      return { success: false, error: 'Invalid response from server' };
    }
    return {
      success: true,
      accessToken: data.accessToken,
      csrfToken: data.csrfToken,
    };
  } catch (err: unknown) {
    return { success: false, error: extractErrorMessage(err, 'Failed to change password') };
  }
}

function extractErrorMessage(err: unknown, fallback: string): string {
  if (err && typeof err === 'object' && 'response' in err) {
    const axiosError = err as { response?: { data?: ProblemDetails } };
    const data = axiosError.response?.data;
    if (data && typeof data === 'object' && ('detail' in data || 'title' in data)) {
      return (data.detail as string) ?? (data.title as string) ?? fallback;
    }
  }
  return 'Network error. Please try again.';
}

export async function login(credentials: LoginCredentials): Promise<LoginResult> {
  try {
    const response = await apiClient.post<{
      accessToken: string | null;
      csrfToken: string | null;
      expiresInSeconds: number | null;
      otpRequired: boolean;
      otpSessionId: string | null;
    }>('/users/login', {
      email: credentials.email,
      password: credentials.password,
    });
    const data = response.data;
    if (!data || typeof data !== 'object') {
      return { success: false, error: 'Invalid response from server' };
    }
    if (data.otpRequired && data.otpSessionId) {
      return { success: true, otpRequired: true, otpSessionId: data.otpSessionId };
    }
    if (!data.accessToken || !data.csrfToken) {
      return { success: false, error: 'Invalid response from server' };
    }
    return {
      success: true,
      accessToken: data.accessToken,
      csrfToken: data.csrfToken,
      expiresInSeconds:
        typeof data.expiresInSeconds === 'number' && data.expiresInSeconds > 0
          ? data.expiresInSeconds
          : 15 * 60,
    };
  } catch (err: unknown) {
    if (err && typeof err === 'object' && 'response' in err) {
      const axiosError = err as { response?: { data?: ProblemDetails; status?: number } };
      const data = axiosError.response?.data;
      if (data && typeof data === 'object' && ('detail' in data || 'title' in data)) {
        return { success: false, error: resolveProblemDetailsMessage(data, 'Unable to sign in. Please try again.') };
      }
    }
    return { success: false, error: 'Network error. Please try again.' };
  }
}

export type RefreshResult =
  | { success: true; accessToken: string; csrfToken: string; expiresInSeconds: number }
  | { success: false; error: string };

export async function refreshTokenApi(request: {
  accessToken: string | null;
  csrfToken: string;
}): Promise<RefreshResult> {
  const maxAttempts = 2;
  const retryDelayMs = 250;

  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    try {
      const response = await apiClient.post<AccessTokenResponse>(
        '/users/refresh',
        request.accessToken ? { accessToken: request.accessToken } : {},
        {
          headers: { 'X-CSRF-TOKEN': request.csrfToken },
          _skipAuthRefresh: true,
        } as Parameters<typeof apiClient.post>[2]
      );
      const data = response.data;
      if (!data || typeof data !== 'object' || !data.accessToken || !data.csrfToken) {
        return { success: false, error: 'Invalid response from server' };
      }
      return {
        success: true,
        accessToken: data.accessToken,
        csrfToken: data.csrfToken,
        expiresInSeconds:
          typeof data.expiresInSeconds === 'number' && data.expiresInSeconds > 0
            ? data.expiresInSeconds
            : 15 * 60,
      };
    } catch {
      if (attempt < maxAttempts - 1) {
        await new Promise((resolve) => {
          window.setTimeout(resolve, retryDelayMs);
        });
        continue;
      }

      return { success: false, error: 'Token refresh failed' };
    }
  }

  return { success: false, error: 'Token refresh failed' };
}

export async function logoutApi(): Promise<void> {
  try {
    await apiClient.post('/users/logout');
  } catch {
    // Ignore errors during logout - token will expire anyway
  }
}

export type VerifyOtpResult =
  | { success: true; accessToken: string; csrfToken: string; expiresInSeconds: number }
  | { success: false; error: string };

export async function verifyOtpApi(
  otpSessionId: string,
  code: string
): Promise<VerifyOtpResult> {
  try {
    const response = await apiClient.post<AccessTokenResponse>('/users/verify-otp', {
      otpSessionId,
      code,
    });
    const data = response.data;
    if (!data || typeof data !== 'object' || !data.accessToken || !data.csrfToken) {
      return { success: false, error: 'Invalid response from server' };
    }
    return {
      success: true,
      accessToken: data.accessToken,
      csrfToken: data.csrfToken,
      expiresInSeconds:
        typeof data.expiresInSeconds === 'number' && data.expiresInSeconds > 0
          ? data.expiresInSeconds
          : 15 * 60,
    };
  } catch (err: unknown) {
    if (err && typeof err === 'object' && 'response' in err) {
      const axiosError = err as { response?: { data?: ProblemDetails; status?: number } };
      const data = axiosError.response?.data;
      if (data && typeof data === 'object' && ('detail' in data || 'title' in data)) {
        return { success: false, error: resolveProblemDetailsMessage(data, 'Verification failed') };
      }
    }
    return { success: false, error: 'Network error. Please try again.' };
  }
}

export type TwoFactorInitiateResult =
  | { success: true; otpSessionId: string }
  | { success: false; error: string };

export async function initiateTwoFactorApi(): Promise<TwoFactorInitiateResult> {
  try {
    const response = await apiClient.post<{ otpSessionId: string }>(
      '/users/two-factor/initiate'
    );
    return { success: true, otpSessionId: response.data.otpSessionId };
  } catch (err: unknown) {
    if (err && typeof err === 'object' && 'response' in err) {
      const axiosError = err as { response?: { data?: ProblemDetails; status?: number } };
      const data = axiosError.response?.data;
      if (data && typeof data === 'object' && ('detail' in data || 'title' in data)) {
        const message = (data.detail as string) ?? (data.title as string) ?? 'Failed to initiate 2FA';
        return { success: false, error: message };
      }
    }
    return { success: false, error: 'Network error. Please try again.' };
  }
}

export async function verifyTwoFactorApi(
  otpSessionId: string,
  code: string
): Promise<{ success: true } | { success: false; error: string }> {
  try {
    await apiClient.post('/users/two-factor/verify', { otpSessionId, code });
    return { success: true };
  } catch (err: unknown) {
    if (err && typeof err === 'object' && 'response' in err) {
      const axiosError = err as { response?: { data?: ProblemDetails; status?: number } };
      const data = axiosError.response?.data;
      if (data && typeof data === 'object' && ('detail' in data || 'title' in data)) {
        return { success: false, error: resolveProblemDetailsMessage(data, 'Verification failed') };
      }
    }
    return { success: false, error: 'Network error. Please try again.' };
  }
}

export async function disableTwoFactorApi(): Promise<
  { success: true } | { success: false; error: string }
> {
  try {
    await apiClient.post('/users/two-factor/disable');
    return { success: true };
  } catch (err: unknown) {
    if (err && typeof err === 'object' && 'response' in err) {
      const axiosError = err as { response?: { data?: ProblemDetails; status?: number } };
      const data = axiosError.response?.data;
      if (data && typeof data === 'object' && ('detail' in data || 'title' in data)) {
        const message = (data.detail as string) ?? (data.title as string) ?? 'Failed to disable 2FA';
        return { success: false, error: message };
      }
    }
    return { success: false, error: 'Network error. Please try again.' };
  }
}

export type ResendOtpResult =
  | { success: true; otpSessionId: string }
  | { success: false; error: string };

export async function resendOtpApi(otpSessionId: string): Promise<ResendOtpResult> {
  try {
    const response = await apiClient.post<{ otpSessionId: string }>(
      '/users/resend-otp',
      { otpSessionId }
    );
    return { success: true, otpSessionId: response.data.otpSessionId };
  } catch (err: unknown) {
    if (err && typeof err === 'object' && 'response' in err) {
      const axiosError = err as { response?: { data?: ProblemDetails; status?: number } };
      const data = axiosError.response?.data;
      if (data && typeof data === 'object' && ('detail' in data || 'title' in data)) {
        const message = (data.detail as string) ?? (data.title as string) ?? 'Failed to resend code';
        return { success: false, error: message };
      }
    }
    return { success: false, error: 'Network error. Please try again.' };
  }
}
